import { isDefined } from "@clipboard-health/util-ts";
import { CardContent, DialogContent, Stack } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import {
  BottomSheet,
  Card,
  CbhIcon,
  PageHeaderWrapper,
  Text,
  Title,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { useMemo, useState } from "react";

import { Button } from "../../components/Button";
import { DialogFooter } from "../../components/DialogFooter";
import { useInHomeVisitModalsDataContext } from "../../ShiftDiscovery/useInHomeVisitModalsDataContext";

interface LogVisitBottomSheetProps {
  modalState: UseModalState;
  visitDate: Date | undefined;
  onConfirm: (date: Date) => void;
}

export function LogVisitBottomSheet(props: LogVisitBottomSheetProps) {
  const { modalState, visitDate, onConfirm } = props;
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(visitDate);
  const { visitOccurrences } = useInHomeVisitModalsDataContext();

  const hasVisitOccurrenceForSelectedDate = useMemo(() => {
    if (!isDefined(selectedDate)) {
      return false;
    }

    return (
      visitOccurrences.some((visitOccurrence) => {
        const occurrenceDate = new Date(visitOccurrence.attributes.completedAt);
        return (
          occurrenceDate.getDate() === selectedDate?.getDate() &&
          occurrenceDate.getMonth() === selectedDate.getMonth() &&
          occurrenceDate.getFullYear() === selectedDate.getFullYear()
        );
      }) ?? false
    );
  }, [selectedDate, visitOccurrences]);

  return (
    <BottomSheet
      modalState={modalState}
      footer={
        <DialogFooter orientation="vertical" onClose={modalState.closeModal}>
          <Button
            fullWidth
            variant="contained"
            disabled={!selectedDate}
            onClick={() => {
              if (!isDefined(selectedDate)) {
                return;
              }

              onConfirm(selectedDate);
            }}
          >
            Next
          </Button>
        </DialogFooter>
      }
    >
      <DialogContent>
        <Stack spacing={4} alignItems="center" width="100%">
          <PageHeaderWrapper variant="tertiary" />
          <Title component="h5" variant="h5" sx={{ textAlign: "center" }}>
            Log a visit
          </Title>
          <Title semibold variant="h3" component="h3">
            Select arrival date
          </Title>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <StaticDatePicker
              disableFuture
              value={selectedDate}
              slotProps={{
                calendarHeader: { sx: { display: "flex", justifyContent: "space-around" } },
                toolbar: { hidden: true },
                actionBar: { hidden: true },
              }}
              sx={(theme) => ({
                backgroundColor: theme.palette.background.tertiary,
              })}
              onChange={(newValue) => {
                setSelectedDate(newValue ?? undefined);
              }}
            />
          </LocalizationProvider>

          {hasVisitOccurrenceForSelectedDate && (
            <Card
              sx={{
                backgroundColor: (theme) => theme.palette.intent?.warning.background,
                color: (theme) => theme.palette.intent?.warning.text,
              }}
            >
              <CardContent>
                <Stack direction="row" spacing={2}>
                  <CbhIcon type="triangle-exclamation" size="small" />
                  <Text variant="body2">
                    It looks like you&apos;re trying to log a visit for a date that already has one.
                    Please make sure this is intentional to avoid creating a duplicate.
                  </Text>
                </Stack>
              </CardContent>
            </Card>
          )}
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
